// Field manager page (no route loader)
// - Validates `:managerId` and `?edit` locally
// - If `managerId === "new"` without ?edit => redirect to `/` (old loader behavior)
// - Shows Spinner only while the record could still arrive
// - Renders <h1> error if manager is not found

import { useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import Spinner from '../components/Spinner';
import FieldManagerCard from '../components/FieldManagerCard';
import { useUser } from '../utils/hooks';
import { UsersRoles } from '../utils/enums';

export default function PageFieldManager() {
  const { managerId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { entity: found, isResolved } = useUser(managerId);

  // Handle "create new" route: /field-managers/new
  useEffect(() => {
    // If user navigates to "new" without ?edit, redirect to home (same as old loader)
    if (managerId === 'new') {
      const editParam = searchParams.get('edit');
      if (!editParam) navigate(-1, { replace: true });
    }
  }, [managerId, searchParams, navigate]);

  // Case: creating a new manager with ?edit present
  if (managerId === 'new') {
    const editParam = searchParams.get('edit');

    // If redirect just triggered (no ?edit), keep UI calm with a spinner
    if (!editParam) {
      return (
        <Layout screenSized className='page-field-manager'>
          <Spinner show />
        </Layout>
      );
    }

    // Render creation mode; pass minimal shape if your card expects an object
    return (
      <Layout screenSized className='page-field-manager' trail>
        <FieldManagerCard manager={{ id: 'new' }} />
      </Layout>
    );
  }

  // Find an existing manager by id and role
  const manager = found?.role === UsersRoles.FIELDMANAGER ? found : undefined;

  // Spinner only while the record could still show up — the current user's own
  // card is already in usersById, so it renders without waiting for the list
  if (!manager && !isResolved) {
    return (
      <Layout screenSized className='page-field-manager'>
        <Spinner show />
      </Layout>
    );
  }

  // Not found => plain H1 error
  if (!manager) {
    return (
      <Layout screenSized className='page-field-manager'>
        <h1>{`Error: no route matches url "/field-managers/${managerId}"`}</h1>
      </Layout>
    );
  }

  // Happy path
  return (
    <Layout className='page-field-manager' trail>
      <FieldManagerCard manager={manager} />
    </Layout>
  );
}
